import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  History,
  RotateCcw,
  Scale,
  XCircle,
} from "lucide-react";

const IMPORTANCE_LABELS = {
  unknown: "Importance not set",
  nice_to_have: "Nice to have",
  important: "Important",
  very_important: "Very important",
  deal_breaker: "Deal-breaker",
};

const CERTAINTY_LABELS = {
  unknown: "Certainty not set",
  exploring: "Exploring",
  leaning: "Probably true",
  pretty_sure: "Pretty sure",
  certain: "Certain",
};


const TRADEOFF_DECISIONS = {
  accept: {
    label: "You decided this was worth it.",
    badge: "Worth it",
    className: "bg-emerald-100 text-emerald-800",
  },
  maybe: {
    label: "You weren't sure yet.",
    badge: "Not sure yet",
    className: "bg-amber-100 text-amber-900",
  },
  reject: {
    label: "You decided this was too much.",
    badge: "Too much",
    className: "bg-rose-100 text-rose-800",
  },
};

function fmtUsd(value) {
  if (typeof value !== "number") return null;
  return "$" + Math.round(value).toLocaleString() + "/month";
}

function fmtDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return null;
  }
}

function versionSummary(version, previous) {
  if (!previous) {
    return [
      CERTAINTY_LABELS[version.certainty] || version.certainty,
      IMPORTANCE_LABELS[version.importance] || version.importance,
    ].filter(Boolean);
  }

  const bits = [];
  if (version.importance !== previous.importance) {
    bits.push(
      "Importance: " +
        (IMPORTANCE_LABELS[previous.importance] || previous.importance) +
        " → " +
        (IMPORTANCE_LABELS[version.importance] || version.importance)
    );
  }
  if (version.certainty !== previous.certainty) {
    bits.push(
      "Certainty: " +
        (CERTAINTY_LABELS[previous.certainty] || previous.certainty) +
        " → " +
        (CERTAINTY_LABELS[version.certainty] || version.certainty)
    );
  }
  return bits.length ? bits : ["You reconfirmed this Realisation."];
}

export default function RealisationJourney({ realisationId }) {
  const [open, setOpen] = useState(false);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const loadJourney = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await api.get(
        "/realisations/" + realisationId + "/journey"
      );
      setJourney(data);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || journey || loading) return;
    await loadJourney();
  };

  useEffect(() => {
    const refresh = (event) => {
      if (event?.detail?.realisationId !== realisationId) return;
      setJourney(null);
      if (open) loadJourney();
    };

    window.addEventListener("wua:realisations-tradeoff-changed", refresh);
    return () =>
      window.removeEventListener(
        "wua:realisations-tradeoff-changed",
        refresh
      );
    // Capture open so an already-open journey refreshes after a decision change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realisationId, open]);

  return (
    <div className="mt-5 border-t border-stone-200 pt-4">
      <button
        type="button"
        onClick={toggle}
        data-testid={"realisation-journey-toggle-" + realisationId}
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#4C1D95] hover:text-[#3B0764]"
      >
        <History className="h-4 w-4" />
        How you got here
        {open ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
      </button>

      {open && (
        <div
          className="mt-4 rounded-md border border-stone-200 bg-stone-50/60 p-4 sm:p-5"
          data-testid={"realisation-journey-" + realisationId}
        >
          {loading && (
            <div className="text-sm text-stone-500">Loading your journey…</div>
          )}

          {failed && (
            <div className="text-sm text-rose-700">
              We couldn't load this journey just now.
            </div>
          )}

          {journey && (
            <div className="space-y-6">
              <section>
                <div className="text-[10px] font-underground-sc tracking-[0.14em] text-stone-500">
                  What WUA noticed
                </div>
                {journey.evidence?.length ? (
                  <div className="mt-3 grid gap-2">
                    {journey.evidence.map((item, index) => (
                      <div
                        key={index}
                        className="rounded-md border border-stone-200 bg-white px-3.5 py-3"
                      >
                        <div className="text-xs font-semibold text-[#4C1D95]">
                          {item.reason_label}
                        </div>
                        <div className="mt-0.5 text-sm font-semibold text-stone-900">
                          {item.title}
                        </div>
                        {item.subtitle && (
                          <div className="text-xs text-stone-500">
                            {item.subtitle}
                          </div>
                        )}
                        {fmtDate(item.occurred_at) && (
                          <div className="mt-1 text-[11px] text-stone-400">
                            {fmtDate(item.occurred_at)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-stone-500">
                    This Realisation came from your decision journey, but the
                    original choice labels are not available.
                  </p>
                )}
              </section>

              <section>
                <div className="text-[10px] font-underground-sc tracking-[0.14em] text-stone-500">
                  How it changed
                </div>
                <div className="mt-3 space-y-0">
                  {(journey.versions || []).map((version, index) => {
                    const previous = journey.versions[index - 1];
                    const isFirst = index === 0;
                    return (
                      <div
                        key={version.realisation_id || index}
                        className="relative flex gap-3 pb-5 last:pb-0"
                      >
                        {index < journey.versions.length - 1 && (
                          <div className="absolute left-[7px] top-5 bottom-0 w-px bg-stone-200" />
                        )}
                        <div className="relative z-10 mt-1 shrink-0">
                          {isFirst ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <RotateCcw className="h-4 w-4 text-[#4C1D95]" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-stone-900">
                            {isFirst ? "You confirmed this" : "You refined this"}
                          </div>
                          <div className="mt-0.5 text-xs text-stone-500">
                            {versionSummary(version, previous).join(" · ")}
                          </div>
                          {fmtDate(version.confirmed_at) && (
                            <div className="mt-1 text-[11px] text-stone-400">
                              {fmtDate(version.confirmed_at)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {journey.current_state === "withdrawn" && (
                    <div className="relative flex gap-3 pt-1">
                      <div className="relative z-10 mt-1 shrink-0">
                        <XCircle className="h-4 w-4 text-rose-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-stone-900">
                          You said this was no longer true
                        </div>
                        {fmtDate(
                          journey.versions?.[journey.versions.length - 1]
                            ?.withdrawn_at
                        ) && (
                          <div className="mt-1 text-[11px] text-stone-400">
                            {fmtDate(
                              journey.versions?.[journey.versions.length - 1]
                                ?.withdrawn_at
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {journey.tradeoffs?.length > 0 && (
                <section data-testid={"realisation-tradeoffs-" + realisationId}>
                  <div className="flex items-center gap-1.5 text-[10px] font-underground-sc tracking-[0.14em] text-stone-500">
                    <Scale className="h-3.5 w-3.5" />
                    Trade-offs you considered
                  </div>
                  <div className="mt-3 grid gap-3">
                    {journey.tradeoffs.map((item, index) => {
                      const decision =
                        TRADEOFF_DECISIONS[item.response] ||
                        TRADEOFF_DECISIONS.maybe;
                      const monthlyCost = fmtUsd(item.monthly_cost_usd);
                      const monthlyBudget = fmtUsd(item.monthly_budget_usd);
                      const overshoot = fmtUsd(item.overshoot_usd);
                      const reconsidered = journey.tradeoffs
                        .slice(0, index)
                        .some(
                          (previous) =>
                            previous.university_name === item.university_name &&
                            previous.city_name === item.city_name &&
                            Boolean(item.university_name || item.city_name)
                        );

                      return (
                        <div
                          key={index}
                          className="min-w-0 break-words [overflow-wrap:anywhere] rounded-md border border-stone-200 bg-white px-3.5 py-3.5"
                          data-testid={
                            "realisation-tradeoff-" + realisationId + "-" + index
                          }
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={
                                "rounded-full px-2 py-0.5 text-[10px] font-semibold " +
                                decision.className
                              }
                            >
                              {decision.badge}
                            </span>
                            {item.city_name && (
                              <span className="text-xs font-semibold text-[#4C1D95]">
                                {item.city_name}
                              </span>
                            )}
                          </div>

                          {item.statement && (
                            <div className="mt-2 text-sm font-semibold text-stone-900">
                              {item.statement}
                            </div>
                          )}

                          {(item.university_name || item.city_name) && (
                            <div className="mt-1 text-xs text-stone-500">
                              {[item.university_name, item.city_name]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          )}

                          {reconsidered && (
                            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#4C1D95]">
                              <RotateCcw className="h-3.5 w-3.5" />
                              You changed your mind
                            </div>
                          )}

                          <div
                            className={
                              reconsidered
                                ? "mt-1 text-sm text-stone-700"
                                : "mt-2 text-sm text-stone-700"
                            }
                          >
                            {decision.label}
                          </div>

                          {monthlyCost && monthlyBudget && (
                            <div className="mt-1.5 text-xs text-stone-500">
                              At the time: about {monthlyCost} against a{" "}
                              {monthlyBudget} living budget
                              {overshoot ? " (" + overshoot + " over)." : "."}
                            </div>
                          )}

                          {fmtDate(item.decided_at) && (
                            <div className="mt-1.5 text-[11px] text-stone-400">
                              {fmtDate(item.decided_at)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
